import React, { useState, useEffect } from 'react';
import { propertyAPI, Property } from '../services/propertyAPI';
import { locationAPI, Location } from '../services/locationAPI';
import { withLocationRates } from '../utils/locationRates';
import TaxAssumptionNote from './TaxAssumptionNote';
import AssumedDefaultsNotice from './AssumedDefaultsNotice';
import { parseGrowthRates, missingPricesAsOf } from '../utils/growthRates';
import { toEngineMortgage } from '../utils/mortgageVariants';

const fmtEur = (v: number) => '€' + Math.round(v).toLocaleString();
const fmtPct = (v: number | undefined) => (v === undefined ? '–' : `${v.toFixed(1)}%`);

// Histogram of total earnings over the horizon (SVG, no chart library).
const Histogram: React.FC<{ histogram: any; mean: number }> = ({ histogram, mean }) => {
  const { bin_edges, counts } = histogram;
  const W = 700, H = 220, padL = 10, padR = 10, padT = 10, padB = 28;
  const n = counts.length;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const maxCount = Math.max(...counts, 1);
  const bw = plotW / n;
  const xMin = bin_edges[0], xMax = bin_edges[n];
  const xPix = (v: number) => padL + ((v - xMin) / ((xMax - xMin) || 1)) * plotW;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {counts.map((c: number, i: number) => {
        const h = (c / maxCount) * plotH;
        return <rect key={i} x={padL + i * bw} y={padT + plotH - h}
          width={Math.max(bw - 1, 1)} height={h} fill="#7c3aed" opacity={0.85} />;
      })}
      {xMin < 0 && xMax > 0 && (
        <line x1={xPix(0)} y1={padT} x2={xPix(0)} y2={padT + plotH}
          stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
      )}
      <line x1={xPix(mean)} y1={padT} x2={xPix(mean)} y2={padT + plotH}
        stroke="#111827" strokeWidth={1.5} />
      <text x={padL} y={H - 8} fontSize="11" fill="#6b7280">{fmtEur(xMin)}</text>
      <text x={xPix(mean)} y={H - 8} fontSize="11" fill="#111827" textAnchor="middle">mean {fmtEur(mean)}</text>
      <text x={W - padR} y={H - 8} fontSize="11" fill="#6b7280" textAnchor="end">{fmtEur(xMax)}</text>
    </svg>
  );
};

// Fan chart of cumulative earnings by year: p5–p95 and p25–p75 bands, median,
// and the deterministic base.
const FanChart: React.FC<{ fan: any }> = ({ fan }) => {
  const { years, p5, p25, p50, p75, p95, base } = fan;
  const W = 700, H = 280, padL = 75, padR = 12, padT = 12, padB = 28;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const all = [...p5, ...p95, ...base];
  const yMin = Math.min(...all), yMax = Math.max(...all);
  const xPix = (i: number) => padL + (years.length === 1 ? plotW / 2 : (i / (years.length - 1)) * plotW);
  const yPix = (v: number) => padT + plotH - ((v - yMin) / ((yMax - yMin) || 1)) * plotH;
  const bandPts = (lo: number[], hi: number[]) =>
    hi.map((v: number, i: number) => `${xPix(i)},${yPix(v)}`).join(' ') + ' ' +
    lo.map((v: number, i: number) => `${xPix(i)},${yPix(v)}`).reverse().join(' ');
  const linePts = (arr: number[]) => arr.map((v, i) => `${xPix(i)},${yPix(v)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <polygon points={bandPts(p5, p95)} fill="#a78bfa" opacity={0.25} />
      <polygon points={bandPts(p25, p75)} fill="#7c3aed" opacity={0.3} />
      <polyline points={linePts(p50)} fill="none" stroke="#5b21b6" strokeWidth={2} />
      <polyline points={linePts(base)} fill="none" stroke="#374151" strokeWidth={1.5} strokeDasharray="5 4" />
      {yMin < 0 && yMax > 0 && (
        <line x1={padL} y1={yPix(0)} x2={W - padR} y2={yPix(0)} stroke="#ef4444" strokeWidth={1} strokeDasharray="3 3" />
      )}
      {years.map((yr: number, i: number) => (
        <text key={yr} x={xPix(i)} y={H - 8} fontSize="11" fill="#6b7280" textAnchor="middle">{yr}</text>
      ))}
      <text x={4} y={yPix(yMax) + 9} fontSize="11" fill="#6b7280">{fmtEur(yMax)}</text>
      <text x={4} y={yPix(yMin)} fontSize="11" fill="#6b7280">{fmtEur(yMin)}</text>
    </svg>
  );
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="text-center">
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-sm text-gray-600">{label}</div>
  </div>
);

const MonteCarloSimulation: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobRunning, setJobRunning] = useState(false);
  // F15: growth rates for the run - required, no default.
  const [euroInflationPct, setEuroInflationPct] = useState<string>('');
  const [rentGrowthPct, setRentGrowthPct] = useState<string>('');
  const [simulationParams, setSimulationParams] = useState({
    numberOfScenarios: 5000,
    timeHorizonYears: 10,
    confidenceLevel: 95,
    includeEconomicShocks: true,
    includeWeatherEvents: true,
    includeRegulatoryChanges: true,
    includeEventCancellations: true,
  });

  useEffect(() => { loadProperties(); loadLocations(); }, []);

  const loadProperties = async () => {
    try {
      setProperties(await propertyAPI.getProperties());
    } catch (e) {
      console.error('Failed to load properties:', e);
    }
  };

  const loadLocations = async () => {
    try {
      setLocations(await locationAPI.getLocations());
    } catch (e) {
      console.error('Failed to load locations:', e);
    }
  };

  const runMonteCarloSimulation = async () => {
    if (properties.length === 0) { alert('Please add properties to your portfolio first'); return; }
    const growth = parseGrowthRates({ euroInflationPct, rentGrowthPct });
    if (!growth.ok) { setError(growth.error); return; }
    const undated = missingPricesAsOf(properties);
    if (undated.length > 0) { setError(`Set "Prices As Of" on: ${undated.join(', ')}`); return; }
    setLoading(true); setError(null); setResults(null); setJobRunning(false);
    const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    const payload = JSON.stringify({
      properties: properties.map(p => withLocationRates(toEngineMortgage(p), locations)),
      simulation_params: simulationParams,
      ...growth.rates,
    });
    try {
      if (simulationParams.numberOfScenarios > 200000) {
        // Large run: hand off to a background job and poll for the result so the
        // request doesn't time out.
        const startResp = await fetch(`${base}/api/v1/portfolio/monte-carlo/jobs`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload,
        });
        if (!startResp.ok) {
          const b = await startResp.json().catch(() => null);
          throw new Error(b?.detail || `HTTP error! status: ${startResp.status}`);
        }
        const { job_id: jobId } = await startResp.json();
        setJobRunning(true);
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 1500));
          const jr = await fetch(`${base}/api/v1/portfolio/monte-carlo/jobs/${jobId}`);
          if (!jr.ok) throw new Error(`Job poll failed: HTTP ${jr.status}`);
          const job = await jr.json();
          if (job.status === 'done') { setResults(job.result); break; }
          if (job.status === 'error') throw new Error(job.error || 'Monte Carlo job failed');
        }
      } else {
        const response = await fetch(`${base}/api/v1/portfolio/monte-carlo`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload,
        });
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw new Error(body?.detail || `HTTP error! status: ${response.status}`);
        }
        setResults(await response.json());
      }
    } catch (e: any) {
      console.error('Monte Carlo simulation failed:', e);
      setError(e?.message || 'Monte Carlo simulation failed. Please try again.');
    } finally {
      setLoading(false);
      setJobRunning(false);
    }
  };

  const s = results?.summary;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Monte Carlo Simulation</h1>
            <p className="mt-2 text-gray-600">
              Probabilistic portfolio outcomes across occupancy, market, currency, and risk-event uncertainty.
            </p>
          </div>
          <a href="/portfolio" className="bg-vista-teal-600 text-white px-4 py-2 rounded-md hover:bg-vista-teal-700 transition-colors">
            Back to Portfolio
          </a>
        </div>

        {/* Portfolio overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Portfolio Overview</h2>
          {properties.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-600">No properties in portfolio. Please add properties first.</div>
              <a href="/portfolio" className="text-vista-teal-600 hover:underline mt-2 inline-block">Go to Portfolio</a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Stat label="Properties" value={`${properties.length}`} color="text-vista-teal-600" />
              <Stat label="Total Investment" value={fmtEur(properties.reduce((a, p) => a + p.purchase_price_eur, 0))} color="text-vista-teal-600" />
              <Stat label="Avg Appreciation" value={`${(properties.reduce((a, p) => a + (p.annual_appreciation_pct || 0), 0) / properties.length).toFixed(1)}%`} color="text-vista-teal-600" />
            </div>
          )}
        </div>

        {/* Parameters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Simulation Parameters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Scenarios</label>
              <input type="number" value={simulationParams.numberOfScenarios} min={100} max={1000000} step={1000}
                onChange={(e) => setSimulationParams(p => ({ ...p, numberOfScenarios: parseInt(e.target.value) || 5000 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500" />
              <p className="text-xs text-gray-500 mt-1">Over 200,000 runs as a background job (up to 1,000,000).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Horizon (Years)</label>
              <input type="number" value={simulationParams.timeHorizonYears} min={1} max={30}
                onChange={(e) => setSimulationParams(p => ({ ...p, timeHorizonYears: parseInt(e.target.value) || 10 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" title="Annual growth of running costs from each property's Prices As Of date">Euro Inflation (%/yr)</label>
              <input type="number" step="0.1" placeholder="required, e.g. 2" value={euroInflationPct}
                onChange={(e) => setEuroInflationPct(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" title="Annual growth of nightly and event rates from each property's Prices As Of date">Rent Growth (%/yr)</label>
              <input type="number" step="0.1" placeholder="required, e.g. 2" value={rentGrowthPct}
                onChange={(e) => setRentGrowthPct(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confidence Level (%)</label>
              <input type="number" value={simulationParams.confidenceLevel} min={80} max={99}
                onChange={(e) => setSimulationParams(p => ({ ...p, confidenceLevel: parseInt(e.target.value) || 95 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500" />
            </div>
            {[
              ['includeEconomicShocks', 'Economic, market & currency shocks'],
              ['includeWeatherEvents', 'Weather variability'],
              ['includeRegulatoryChanges', 'Regulatory & property risk events'],
              ['includeEventCancellations', 'Event cancellations (each event\'s probability)'],
            ].map(([key, label]) => (
              <div className="flex items-center" key={key}>
                <input type="checkbox" id={key} checked={(simulationParams as any)[key]}
                  onChange={(e) => setSimulationParams(p => ({ ...p, [key]: e.target.checked }))}
                  className="h-4 w-4 text-vista-teal-600 focus:ring-vista-teal-500 border-gray-300 rounded" />
                <label htmlFor={key} className="ml-2 block text-sm text-gray-900">{label}</label>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <button onClick={runMonteCarloSimulation} disabled={loading || properties.length === 0}
              className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Running Simulation…' : 'Run Monte Carlo Simulation'}
            </button>
            {jobRunning && <span className="ml-4 text-purple-700 text-sm">Large run in progress as a background job — polling for the result…</span>}
            {error && <span className="ml-4 text-red-600 text-sm">{error}</span>}
          </div>
        </div>

        {/* Results */}
        {s && (
          <>
            <TaxAssumptionNote className="mb-8" />
            <AssumedDefaultsNotice assumed={results?.assumed_defaults} className="mb-8" />
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Outcome Summary <span className="text-sm font-normal text-gray-500">({results.n_runs.toLocaleString()} runs)</span>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Stat label="Expected total" value={fmtEur(s.mean_total)} color="text-green-600" />
                <Stat label={s.cash_invested ? `Return on ${fmtEur(s.cash_invested)} cash, over ${s.years} years` : "Return on cash"} value={fmtPct(s.return_on_cash_over_hold_pct)} color="text-green-600" />
                <Stat label={`Value at Risk (${s.confidence_pct}%)`} value={fmtEur(s.value_at_risk)} color="text-red-600" />
                <Stat label="Probability of loss" value={`${(s.prob_loss * 100).toFixed(1)}%`} color="text-orange-600" />
                <Stat label="Std deviation" value={fmtEur(s.std_total)} color="text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-1">Cumulative earnings fan chart</h3>
              <p className="text-sm text-gray-500 mb-3">
                Median (solid) and deterministic base (dashed); bands show the 25–75% and 5–95% ranges.
              </p>
              <FanChart fan={results.fan_chart} />
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-1">Distribution of total earnings</h3>
              <p className="text-sm text-gray-500 mb-3">
                Red dashed line = break-even; black line = mean. p5 {fmtEur(s.p5_total)} · median {fmtEur(s.median_total)} · p95 {fmtEur(s.p95_total)}.
              </p>
              <Histogram histogram={results.histogram} mean={s.mean_total} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonteCarloSimulation;
